(() => {
  const MIRROR_COOKIE = 'satellai_consent';
  const MIRROR_MAX_AGE = 60 * 60 * 24 * 365;
  const COOKIE_POLICY_URL = 'https://satellai.com/pages/cookie-policy';
  const EUROPEAN_COUNTRIES = new Set([
    'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI', 'LT', 'LU',
    'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SJ',
    'SK', 'SM', 'TR', 'UA', 'VA', 'XK'
  ]);

  /**
   * 调整 Shopify 原生 Customer Privacy 横幅的前台文案。
   * 同意状态由 Shopify 原生脚本保存，并同步到 Google Consent Mode。
   */
  class SatellaiShopifyPrivacyBanner {
    constructor() {
      this.observer = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleConsentChange = this.handleConsentChange.bind(this);
    }

    init() {
      document.addEventListener('click', this.handleClick);
      document.addEventListener('visitorConsentCollected', this.handleConsentChange);
      this.syncExistingConsent();
      if (this.applyBannerText()) {
        this.applyPreferencesText();
        return;
      }

      // Shopify 的横幅是异步注入的，找到后立即停止观察，避免长期监听 DOM。
      this.observer = new MutationObserver(() => {
        if (!this.applyBannerText()) return;
        this.applyPreferencesText();
        this.observer.disconnect();
        this.observer = null;
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    applyBannerText() {
      const settingsButton = document.getElementById('shopify-pc__banner__btn-manage-prefs');
      const acceptButton = document.getElementById('shopify-pc__banner__btn-accept');
      if (!settingsButton || !acceptButton) return false;

      this.setButtonText(settingsButton, 'Cookie Settings');
      this.setButtonText(acceptButton, 'Accept all');
      this.ensureCookiePolicyLink();
      return true;
    }

    /** 在 Shopify 原生文案的 Privacy Policy 后追加 Cookie Policy，并新窗口打开。 */
    ensureCookiePolicyLink() {
      if (document.querySelector('[data-satellai-cookie-policy]')) return;

      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return;

      const privacyPolicyLink = banner.querySelector('a[href*="privacy-policy"]');
      if (!privacyPolicyLink) return;

      const cookiePolicyLink = document.createElement('a');
      cookiePolicyLink.href = COOKIE_POLICY_URL;
      cookiePolicyLink.target = '_blank';
      cookiePolicyLink.rel = 'noopener noreferrer';
      cookiePolicyLink.textContent = 'Cookie Policy';
      cookiePolicyLink.dataset.satellaiCookiePolicy = 'true';

      // `after()` 保持原文末尾的句号在新链接之后，形成
      // “Privacy Policy and Cookie Policy.”。
      privacyPolicyLink.after(' and ', cookiePolicyLink);

      const followingText = cookiePolicyLink.nextSibling;
      if (!followingText || followingText.nodeType !== Node.TEXT_NODE || !/^\s*\./.test(followingText.textContent)) {
        cookiePolicyLink.insertAdjacentText('afterend', '.');
      }
    }

    applyPreferencesText() {
      const acceptButton = document.getElementById('shopify-pc__prefs__header-accept');
      if (acceptButton) this.setButtonText(acceptButton, 'Accept all');
    }

    /** 优先更新 Shopify 按钮内的 span，保留原有 DOM 结构和焦点样式。 */
    setButtonText(button, text) {
      const textElement = button.querySelector('span') || button;
      if (textElement.textContent.trim() !== text) textElement.textContent = text;
    }

    handleClick(event) {
      const settingsButton = event.target.closest('#shopify-pc__banner__btn-manage-prefs');
      if (!settingsButton) return;

      // 偏好弹窗可能在点击后才生成，下一帧再更新其按钮文案。
      requestAnimationFrame(() => this.applyPreferencesText());
    }

    /**
     * 页面首次访问时保持默认同意；若用户曾经做过选择，
     * 则用 Shopify 保存的显式结果覆盖默认值。
     */
    syncExistingConsent() {
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;
        const privacy = window.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return;

        try {
          const consent = privacy.currentVisitorConsent();
          const values = [consent.analytics, consent.marketing, consent.preferences];
          const hasExplicitChoice = values.some((value) => value === 'yes' || value === 'no');
          if (!hasExplicitChoice) {
            this.applyEuropeanDefault(privacy);
            return;
          }

          this.updateConsentMode({
            analytics: consent.analytics !== 'no',
            marketing: consent.marketing !== 'no',
            preferences: consent.preferences !== 'no'
          });
        } catch (error) {
          // Shopify API 读取失败时继续使用站点的默认同意策略
        }
      });
    }

    /**
     * Customer Privacy API 的 getRegion() 使用 Shopify 检测到的访客 IP 地区。
     * 欧洲访客在未表态时不继续默认采集，等待其明确同意。
     */
    applyEuropeanDefault(privacy) {
      if (typeof privacy.getRegion !== 'function') return;

      try {
        const region = privacy.getRegion();
        const countryCode = typeof region === 'string' ? region.slice(0, 2).toUpperCase() : '';
        if (!EUROPEAN_COUNTRIES.has(countryCode)) return;

        this.updateConsentMode({ analytics: false, marketing: false, preferences: false }, false);
      } catch (error) {
        // IP 地区获取失败时，依赖首帧的 Liquid 地区判断。
      }
    }

    /** Shopify 在用户保存同意选择后会发出该事件。 */
    handleConsentChange(event) {
      const consent = event.detail;
      if (!consent) return;

      this.updateConsentMode({
        analytics: consent.analyticsAllowed !== false,
        marketing: consent.marketingAllowed !== false,
        preferences: consent.preferencesAllowed !== false
      });
    }

    /** 同步 Google Consent Mode，并写入可在下次首帧读取的镜像 Cookie。 */
    updateConsentMode(choices, persist = true) {
      if (persist) this.writeMirrorCookie(choices);
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };
      }

      window.gtag('consent', 'update', {
        ad_storage: choices.marketing ? 'granted' : 'denied',
        ad_user_data: choices.marketing ? 'granted' : 'denied',
        ad_personalization: choices.marketing ? 'granted' : 'denied',
        analytics_storage: choices.analytics ? 'granted' : 'denied',
        functionality_storage: choices.preferences ? 'granted' : 'denied',
        personalization_storage: choices.preferences ? 'granted' : 'denied'
      });

      window.dataLayer.push({
        event: 'satellai_consent_update',
        consent_granted: choices.analytics && choices.marketing && choices.preferences,
        consent_analytics: choices.analytics,
        consent_marketing: choices.marketing,
        consent_preferences: choices.preferences,
        consent_source: 'shopify_native_banner'
      });
    }

    writeMirrorCookie(choices) {
      const payload = {
        v: 1,
        s: { a: choices.analytics, m: choices.marketing, p: choices.preferences },
        t: Date.now()
      };

      try {
        const value = encodeURIComponent(JSON.stringify(payload));
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie =
          MIRROR_COOKIE + '=' + value + '; Path=/; Max-Age=' + MIRROR_MAX_AGE + '; SameSite=Lax' + secure;
      } catch (error) {
        // 镜像 Cookie 写入失败不影响 Shopify 保存的真实同意结果
      }
    }
  }

  const start = () => {
    const banner = new SatellaiShopifyPrivacyBanner();
    banner.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    requestAnimationFrame(start);
  }
})();
